/**
 * Un libro de Excel (.xlsx) mínimo, sin dependencias.
 *
 * Existe porque el CSV no tiene formato: Excel adivina cada celda con la
 * configuración regional de quien lo abre. Medido con el primer CSV del
 * reporte de expedientes (13-oct-2026): «07/10/2026» entraba como fecha —el
 * 10 de julio, en un Excel con día y mes al revés— y «30/09/2026» como texto,
 * en la misma columna; el DNI como número (a uno que empiece con 0 se le cae
 * el cero) y las columnas al ancho por defecto. En un .xlsx cada celda dice qué
 * es: la fecha es un número con su formato, el DNI es texto.
 *
 * Un .xlsx es un ZIP de XML. Se guarda sin comprimir (método 0): son pocos KB
 * y así no hace falta un deflate. Los estilos son una tabla fija —`ESTILOS`—
 * porque el reporte necesita pocos; uno nuevo se agrega a la tabla, no se arma
 * suelto.
 *
 * Puro: lo corre `tests/unit/test_reporte_de_expedientes.py` en Node y lo abre
 * con openpyxl.
 */

/** Los estilos disponibles; el índice es su posición en `cellXfs`. */
export const ESTILOS = {
  normal: 0,
  encabezado: 1,
  texto: 2,
  textoLargo: 3,
  fecha: 4,
  centrado: 5,
  /** Verde, como `badge-green` del portal. */
  bien: 6,
  /** Ámbar, como `badge-amber`: algo pendiente, no un error. */
  pendiente: 7,
  /** Rojo, como `badge-red`. */
  mal: 8,
  titulo: 9,
  rotulo: 10,
  entero: 11,
  porcentaje: 12,
  tenue: 13,
  /** Texto y fecha sueltos, sin borde: los valores de una hoja de resumen. */
  valor: 14,
  fechaValor: 15,
} as const

export type Estilo = keyof typeof ESTILOS

export interface Celda {
  /** Un número (también las fechas, ver `serialDeExcel`), un texto o nada. */
  v: string | number | null
  estilo?: Estilo
}

export interface Hoja {
  nombre: string
  /** Ancho de cada columna, en caracteres. */
  anchos: number[]
  filas: Celda[][]
  /** Deja fija la primera fila y le pone los filtros de Excel. */
  encabezadoFijo?: boolean
  /** Al imprimir: horizontal y al ancho de una hoja. Sin esto una planilla
   *  ancha sale partida por columnas en varias páginas. */
  imprimirAlAncho?: boolean
}

const VERDE = 'FF0E8445'
const VERDE_CLARO = 'FFE7F6EC'
const VERDE_OSCURO = 'FF0B5E31'
const ROJO_CLARO = 'FFFDECEC'
const ROJO_OSCURO = 'FFB42318'
const AMBAR_CLARO = 'FFFFF4E5'
const AMBAR_OSCURO = 'FF8A5300'
const GRIS_BORDE = 'FFD9DEE3'
const GRIS_TEXTO = 'FF6B7280'

const ESTILOS_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<numFmts count="2"><numFmt numFmtId="164" formatCode="dd/mm/yyyy hh:mm"/><numFmt numFmtId="165" formatCode="0.0%"/></numFmts>
<fonts count="7">
<font><sz val="11"/><name val="Calibri"/><family val="2"/></font>
<font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/><family val="2"/></font>
<font><b/><sz val="11"/><color rgb="${VERDE_OSCURO}"/><name val="Calibri"/><family val="2"/></font>
<font><b/><sz val="11"/><color rgb="${ROJO_OSCURO}"/><name val="Calibri"/><family val="2"/></font>
<font><b/><sz val="14"/><name val="Calibri"/><family val="2"/></font>
<font><sz val="11"/><color rgb="${GRIS_TEXTO}"/><name val="Calibri"/><family val="2"/></font>
<font><b/><sz val="11"/><color rgb="${AMBAR_OSCURO}"/><name val="Calibri"/><family val="2"/></font>
</fonts>
<fills count="6">
<fill><patternFill patternType="none"/></fill>
<fill><patternFill patternType="gray125"/></fill>
<fill><patternFill patternType="solid"><fgColor rgb="${VERDE}"/><bgColor indexed="64"/></patternFill></fill>
<fill><patternFill patternType="solid"><fgColor rgb="${VERDE_CLARO}"/><bgColor indexed="64"/></patternFill></fill>
<fill><patternFill patternType="solid"><fgColor rgb="${ROJO_CLARO}"/><bgColor indexed="64"/></patternFill></fill>
<fill><patternFill patternType="solid"><fgColor rgb="${AMBAR_CLARO}"/><bgColor indexed="64"/></patternFill></fill>
</fills>
<borders count="2">
<border><left/><right/><top/><bottom/><diagonal/></border>
<border><left style="thin"><color rgb="${GRIS_BORDE}"/></left><right style="thin"><color rgb="${GRIS_BORDE}"/></right><top style="thin"><color rgb="${GRIS_BORDE}"/></top><bottom style="thin"><color rgb="${GRIS_BORDE}"/></bottom><diagonal/></border>
</borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="16">
<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
<xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>
<xf numFmtId="49" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1" applyAlignment="1"><alignment horizontal="left" vertical="top"/></xf>
<xf numFmtId="49" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1" applyAlignment="1"><alignment horizontal="left" vertical="top" wrapText="1"/></xf>
<xf numFmtId="164" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="top"/></xf>
<xf numFmtId="49" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="top"/></xf>
<xf numFmtId="49" fontId="2" fillId="3" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="top"/></xf>
<xf numFmtId="49" fontId="6" fillId="5" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="top"/></xf>
<xf numFmtId="49" fontId="3" fillId="4" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="top"/></xf>
<xf numFmtId="0" fontId="4" fillId="0" borderId="0" xfId="0" applyFont="1"/>
<xf numFmtId="0" fontId="2" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment horizontal="left" vertical="top"/></xf>
<xf numFmtId="1" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1" applyAlignment="1"><alignment horizontal="left" vertical="top"/></xf>
<xf numFmtId="165" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1" applyAlignment="1"><alignment horizontal="left" vertical="top"/></xf>
<xf numFmtId="49" fontId="5" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyBorder="1" applyAlignment="1"><alignment horizontal="left" vertical="top"/></xf>
<xf numFmtId="49" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1" applyAlignment="1"><alignment horizontal="left" vertical="top"/></xf>
<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1" applyAlignment="1"><alignment horizontal="left" vertical="top"/></xf>
</cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`

/**
 * La fecha como la guarda Excel: días desde el 30-12-1899, con la hora como
 * fracción. Excel no tiene zonas horarias, así que se pasa la hora que se
 * quiere VER: `desfaseMs` es lo que hay que restarle al UTC (Lima, 5 h).
 */
export function serialDeExcel(ms: number, desfaseMs = 0): number {
  return (ms - desfaseMs) / 86_400_000 + 25_569
}

/** «A», «B», …, «Z», «AA»… */
export function letraDeColumna(i: number): string {
  let n = i + 1
  let s = ''
  while (n > 0) {
    const r = (n - 1) % 26
    s = String.fromCharCode(65 + r) + s
    n = Math.floor((n - 1) / 26)
  }
  return s
}

/** Un carácter de control (salvo tab y saltos de línea) no es XML válido, y
 *  Excel rechaza el archivo ENTERO por uno solo. */
function esDeControl(ch: string): boolean {
  const c = ch.charCodeAt(0)
  return c < 32 && c !== 9 && c !== 10 && c !== 13
}

function escapar(s: string): string {
  return Array.from(s).filter((ch) => !esDeControl(ch)).join('')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

function celdaXml(c: Celda, ref: string): string {
  const s = ESTILOS[c.estilo ?? 'normal']
  if (c.v === null || c.v === '') return `<c r="${ref}" s="${s}"/>`
  if (typeof c.v === 'number') {
    return Number.isFinite(c.v) ? `<c r="${ref}" s="${s}"><v>${c.v}</v></c>` : `<c r="${ref}" s="${s}"/>`
  }
  return `<c r="${ref}" s="${s}" t="inlineStr"><is><t xml:space="preserve">${escapar(c.v)}</t></is></c>`
}

function hojaXml(h: Hoja): string {
  const ancho = Math.max(1, h.anchos.length, ...h.filas.map((f) => f.length))
  const ultima = letraDeColumna(ancho - 1)
  const vista = h.encabezadoFijo
    ? '<sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/>'
      + '<selection pane="bottomLeft" activeCell="A2" sqref="A2"/></sheetView>'
    : '<sheetView workbookViewId="0"/>'
  const cols = h.anchos.length
    ? `<cols>${h.anchos.map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`).join('')}</cols>`
    : ''
  const filas = h.filas.map((fila, r) =>
    `<row r="${r + 1}">${fila.map((c, i) => celdaXml(c, `${letraDeColumna(i)}${r + 1}`)).join('')}</row>`,
  ).join('')
  const filtro = h.encabezadoFijo && h.filas.length > 0
    ? `<autoFilter ref="A1:${ultima}${h.filas.length}"/>` : ''
  // El orden de los elementos lo fija el esquema: `sheetPr` primero y
  // `pageSetup` después de `pageMargins`, o Excel pide reparar el libro.
  return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n'
    + '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">'
    + (h.imprimirAlAncho ? '<sheetPr><pageSetUpPr fitToPage="1"/></sheetPr>' : '')
    + `<dimension ref="A1:${ultima}${Math.max(1, h.filas.length)}"/>`
    + `<sheetViews>${vista}</sheetViews><sheetFormatPr defaultRowHeight="15"/>`
    + `${cols}<sheetData>${filas}</sheetData>${filtro}`
    + '<pageMargins left="0.4" right="0.4" top="0.5" bottom="0.5" header="0.3" footer="0.3"/>'
    + (h.imprimirAlAncho ? '<pageSetup orientation="landscape" fitToWidth="1" fitToHeight="0"/>' : '')
    + '</worksheet>'
}

/** Excel no admite en el nombre de una hoja `: \ / ? * [ ]` ni más de 31 caracteres. */
function nombreDeHoja(nombre: string): string {
  return nombre.replace(/[:\\/?*[\]]/g, ' ').slice(0, 31) || 'Hoja'
}

function libroXml(hojas: Hoja[]): string {
  const filtros = hojas.map((h, i) => (h.encabezadoFijo && h.filas.length > 0
    ? `<definedName name="_xlnm._FilterDatabase" localSheetId="${i}" hidden="1">`
      + `'${escapar(nombreDeHoja(h.nombre)).replace(/'/g, "''")}'!$A$1:$${letraDeColumna(
        Math.max(1, h.anchos.length, ...h.filas.map((f) => f.length)) - 1)}$${h.filas.length}</definedName>`
    : '')).join('')
  return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n'
    + '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" '
    + 'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">'
    + `<sheets>${hojas.map((h, i) =>
      `<sheet name="${escapar(nombreDeHoja(h.nombre))}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('')}</sheets>`
    + (filtros ? `<definedNames>${filtros}</definedNames>` : '')
    + '</workbook>'
}

function partes(hojas: Hoja[]): [string, string][] {
  const n = hojas.length
  const tipos = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n'
    + '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">'
    + '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>'
    + '<Default Extension="xml" ContentType="application/xml"/>'
    + '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>'
    + '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>'
    + hojas.map((_, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" `
      + 'ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>').join('')
    + '</Types>'
  const rels = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n'
    + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
    + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>'
    + '</Relationships>'
  const libroRels = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n'
    + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
    + hojas.map((_, i) => `<Relationship Id="rId${i + 1}" `
      + 'Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" '
      + `Target="worksheets/sheet${i + 1}.xml"/>`).join('')
    + `<Relationship Id="rId${n + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>`
    + '</Relationships>'
  return [
    ['[Content_Types].xml', tipos],
    ['_rels/.rels', rels],
    ['xl/workbook.xml', libroXml(hojas)],
    ['xl/_rels/workbook.xml.rels', libroRels],
    ['xl/styles.xml', ESTILOS_XML],
    ...hojas.map((h, i): [string, string] => [`xl/worksheets/sheet${i + 1}.xml`, hojaXml(h)]),
  ]
}

// ── ZIP sin compresión ───────────────────────────────────────────────────────

const TABLA_CRC = (() => {
  const t = new Uint32Array(256)
  for (let n = 0; n < 256; n++) {
    let c = n
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xEDB88320 ^ (c >>> 1) : c >>> 1
    t[n] = c >>> 0
  }
  return t
})()

function crc32(datos: Uint8Array): number {
  let c = 0xFFFFFFFF
  for (let i = 0; i < datos.length; i++) c = TABLA_CRC[(c ^ datos[i]) & 0xFF] ^ (c >>> 8)
  return (c ^ 0xFFFFFFFF) >>> 0
}

function zip(archivos: [string, Uint8Array][]): Uint8Array<ArrayBuffer> {
  const enc = new TextEncoder()
  const locales: Uint8Array[] = []
  const centrales: Uint8Array[] = []
  let desplazamiento = 0
  for (const [nombre, datos] of archivos) {
    const n = enc.encode(nombre)
    const crc = crc32(datos)
    const local = new Uint8Array(30 + n.length)
    const vl = new DataView(local.buffer)
    vl.setUint32(0, 0x04034B50, true)
    vl.setUint16(4, 20, true)          // versión necesaria
    vl.setUint16(8, 0, true)           // método 0: sin comprimir
    vl.setUint16(12, 0x21, true)       // fecha 1980-01-01, fija: el archivo es reproducible
    vl.setUint32(14, crc, true)
    vl.setUint32(18, datos.length, true)
    vl.setUint32(22, datos.length, true)
    vl.setUint16(26, n.length, true)
    local.set(n, 30)
    const central = new Uint8Array(46 + n.length)
    const vc = new DataView(central.buffer)
    vc.setUint32(0, 0x02014B50, true)
    vc.setUint16(4, 20, true)
    vc.setUint16(6, 20, true)
    vc.setUint16(14, 0x21, true)
    vc.setUint32(16, crc, true)
    vc.setUint32(20, datos.length, true)
    vc.setUint32(24, datos.length, true)
    vc.setUint16(28, n.length, true)
    vc.setUint32(42, desplazamiento, true)
    central.set(n, 46)
    locales.push(local, datos)
    centrales.push(central)
    desplazamiento += local.length + datos.length
  }
  const tamCentral = centrales.reduce((s, c) => s + c.length, 0)
  const fin = new Uint8Array(22)
  const vf = new DataView(fin.buffer)
  vf.setUint32(0, 0x06054B50, true)
  vf.setUint16(8, archivos.length, true)
  vf.setUint16(10, archivos.length, true)
  vf.setUint32(12, tamCentral, true)
  vf.setUint32(16, desplazamiento, true)
  const total = new Uint8Array(desplazamiento + tamCentral + fin.length)
  let p = 0
  for (const parte of [...locales, ...centrales, fin]) { total.set(parte, p); p += parte.length }
  return total
}

/** El .xlsx con las hojas dadas, listo para un `Blob`. */
export function libroXlsx(hojas: Hoja[]): Uint8Array<ArrayBuffer> {
  const enc = new TextEncoder()
  return zip(partes(hojas).map(([nombre, xml]) => [nombre, enc.encode(xml)]))
}

export const TIPO_XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
